import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography
} from "@mui/material";
import produce from "immer";
import React, { JSX, useState } from "react";
import MedicalLabHeader from "../MedicalLabHeader";
import { tl } from "../../../../components/translate";
import UploadStamp from "../../OtherSettings/UploadStamp";
import { notificationAdd } from "../../../../actions/notification";
import { useAppDispatch } from "../../../../store/hooks";
import useServiceProviders from "../../../../hooks/useServiceProviders";
import { spFromIdSelector, spFullNameSelector } from "../../../../reducers/serviceProvider";
import { ResourceCentre } from "../../../../interfaces/ResourceCentreInterface";

interface Props {
  rc: ResourceCentre;
  setRc: (rc: ResourceCentre) => void;
}
const MedicalReportSettings = ({ rc, setRc }: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const serviceProviders = useServiceProviders();

  // refs
  const fileUploader = React.useRef<HTMLInputElement | null>(null);

  // local states
  const [errorMessage, setErrorMessage] = React.useState("");
  const [tempImg, setTempImg] = React.useState<string | null>(null);
  const [isCropperOpen, setIsCropperOpen] = React.useState(false);
  const [customPrintTitle, setCustomPrintTitle] = useState(
    rc.settings.medicalSettings.customPrintTitle || ""
  );

  // memos
  const medicalSp = React.useMemo(
    () => ({
      authorizedSP1:
        spFromIdSelector(serviceProviders, rc.settings.medicalSettings.authorizedSP1) || null,
      authorizedSP2:
        spFromIdSelector(serviceProviders, rc.settings.medicalSettings.authorizedSP2) || null,
      authorizedSP3:
        spFromIdSelector(serviceProviders, rc.settings.medicalSettings.authorizedSP3) || null
    }),
    [rc.settings.medicalSettings, serviceProviders]
  );

  // helper functions
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e?.target?.files?.length) {
      return;
    }
    const file = e.target.files[0];
    const lastDotIndex = file.name.lastIndexOf(".");
    const extension = file.name.substring(lastDotIndex + 1);

    if (file.size >= 200000 || !["jpg", "jpeg", "png", "gif"].includes(extension.toLowerCase())) {
      // show error dialog if file size exceeds 200KB
      // eslint-disable-next-line no-param-reassign
      e.target.value = "";
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "File type not valid or size is greater than 200KB.",
          autoTimeout: true
        })
      );
      return;
    }

    setTempImg(URL.createObjectURL(file));
    setIsCropperOpen(true);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* upload medical lab header */}
      <MedicalLabHeader
        fileUploaderRef={fileUploader}
        onImageChange={handleImageChange}
        isCropperOpen={isCropperOpen}
        setIsCropperOpen={setIsCropperOpen}
        tempImg={tempImg}
        resourceCentre={rc}
        setRc={setRc}
      />

      {/* custom print title */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          sx={{
            pb: "5px",
            fontWeight: 700,
            borderBottom: "1px solid #ededed"
          }}
        >
          {tl("medical.setting.customPrintTitle")}
        </Typography>

        <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
          <Typography sx={{ width: "500px" }}>Print Title</Typography>

          <TextField
            variant="outlined"
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
            placeholder="Provide custom print title"
            value={customPrintTitle}
            onChange={(e) => {
              if (e.target.value.length > 50) {
                setErrorMessage("**Title can not exceed more than 50 characters");
                return;
              }
              setErrorMessage("");
              setCustomPrintTitle(e.target.value);
            }}
            onFocus={() => setErrorMessage("")}
            onBlur={() => {
              if (customPrintTitle.length <= 50) {
                const newRc = produce(rc, (draft) => {
                  draft.settings.medicalSettings.customPrintTitle = customPrintTitle;
                });
                setRc(newRc);
              }
            }}
          />
        </Box>

        {errorMessage && <Typography sx={{ color: "red", my: 2 }}>{errorMessage}</Typography>}
      </Box>

      {/* authorized sp signatures */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ pb: "5px", fontWeight: 700, borderBottom: "1px solid #ededed" }}>
          {tl("authorisedSignatures")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
          <Typography sx={{ width: "150px", flexShrink: 0 }}>
            {tl("authorisedSignature1")}
          </Typography>
          <Autocomplete
            data-testmation="authorizedSP1"
            options={serviceProviders}
            getOptionLabel={(option) => spFullNameSelector(option)}
            renderOption={(props, option) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props} key={option.id}>
                {spFullNameSelector(option)}
              </li>
            )}
            onChange={async (e, v) => {
              const newRc = produce(rc, (draft) => {
                draft.settings.medicalSettings.authorizedSP1 = v ? v.id : null;
              });
              setRc(newRc);
            }}
            value={medicalSp.authorizedSP1}
            style={{ flex: 1 }}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                variant="outlined"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
          <Typography sx={{ width: "150px", flexShrink: 0 }}>
            {tl("authorisedSignature2")}
          </Typography>
          <Autocomplete
            options={serviceProviders}
            getOptionLabel={(option) => spFullNameSelector(option)}
            renderOption={(props, option) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props} key={option.id}>
                {spFullNameSelector(option)}
              </li>
            )}
            style={{ flex: 1 }}
            onChange={async (e, v) => {
              const newRc = produce(rc, (draft) => {
                draft.settings.medicalSettings.authorizedSP2 = v ? v.id : null;
              });
              setRc(newRc);
            }}
            value={medicalSp.authorizedSP2}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                variant="outlined"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
          />
        </Box>
      </Box>

      {/* report options */}
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, borderBottom: "1px solid #ededed" }}>
          {tl("medical.setting.reportOptions")}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={rc.settings.medicalSettings.includeLetterhead}
              onChange={() => {
                const newRc = produce(rc, (draft) => {
                  draft.settings.medicalSettings.includeLetterhead =
                    !rc.settings.medicalSettings.includeLetterhead;
                });
                setRc(newRc);
              }}
            />
          }
          label={tl("clinicSettings.includeLetterheadInMedicalReport")}
        />

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={rc.settings.medicalSettings.showBarCode}
              onChange={() => {
                const newRc = produce(rc, (draft) => {
                  draft.settings.medicalSettings.showBarCode =
                    !rc.settings.medicalSettings.showBarCode;
                });
                setRc(newRc);
              }}
            />
          }
          label={tl("clinicSettings.showBarCodeInMedicalReport")}
        />

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={rc.settings.medicalSettings.showQrCode}
              onChange={() => {
                const newRc = produce(rc, (draft) => {
                  draft.settings.medicalSettings.showQrCode =
                    !rc.settings.medicalSettings.showQrCode;
                });
                setRc(newRc);
              }}
            />
          }
          label={tl("clinicSettings.showQRCodeInMedicalReport")}
        />

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={rc.settings.medicalSettings.showReferrer}
              onChange={() => {
                const newRc = produce(rc, (draft) => {
                  draft.settings.medicalSettings.showReferrer =
                    !rc.settings.medicalSettings.showReferrer;
                });
                setRc(newRc);
              }}
            />
          }
          label={tl("clinicSettings.showReferrerInMedicalReport")}
        />
      </Box>

      {/* upload stamp */}
      <UploadStamp resourceCentre={rc} setRc={setRc} />
    </Box>
  );
};

export default MedicalReportSettings;
